import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchTariff, updateTariffPricebook } from "@/lib/api";
import { PricingForm } from "@/components/PricingForm";
import {
  type PricingFormValues, emptyPricingForm, pricebookToValues, valuesToPricebook,
  isPricebookFree, validatePricingForm,
} from "@/lib/pricingForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface TariffEditorProps {
  unitId: string;
  onSuccess?: () => void;
  inline?: boolean;
}

/**
 * Charger DEFAULT-tariff editor. Uses the ONE shared <PricingForm> + lib/pricingForm
 * serialization — identical fields/validation/pricebook to the Pricing Profiles form.
 * The ONLY difference is the save target: this writes the charger's default pricebook.
 */
export function TariffEditor({ unitId, onSuccess, inline }: TariffEditorProps) {
  const tariffQ = useQuery({
    queryKey: ["unit", unitId, "tariff"],
    queryFn: () => fetchTariff(unitId),
    enabled: !!unitId,
  });

  const [form, setForm] = useState<PricingFormValues>(emptyPricingForm());

  useEffect(() => {
    if (!tariffQ.data) return;
    setForm(pricebookToValues(tariffQ.data.pricebook));
  }, [tariffQ.data]);

  const mutation = useMutation({
    mutationFn: () => {
      const err = validatePricingForm(form);
      if (err) return Promise.reject(new Error(err));
      const pricebook = valuesToPricebook(form);
      return updateTariffPricebook(unitId, isPricebookFree(pricebook) ? "free" : "paid", pricebook);
    },
    onSuccess: () => {
      toast.success("Tariff updated");
      tariffQ.refetch();
      onSuccess?.();
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update tariff"),
  });

  if (tariffQ.isLoading) return <div className="text-sm opacity-60">Loading tariff...</div>;
  if (tariffQ.isError) return <div className="text-sm text-destructive">Failed to load tariff</div>;

  const content = (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        This is the default rate for drivers without a matching pricing profile and for guests.
      </p>
      <PricingForm value={form} onChange={setForm} idPrefix="tariff" />
      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );

  if (inline) return <div className="border-t pt-4 space-y-4">{content}</div>;

  return (
    <Card>
      <CardHeader><CardTitle>Tariff</CardTitle></CardHeader>
      <CardContent className="flex-1 flex flex-col space-y-4">{content}</CardContent>
    </Card>
  );
}
